/**
 * useRoadmapComments
 *
 * Orchestration hook for the roadmap ("Ideas") comments sheet.
 * Mirrors Flutter's roadmap_comments_bottom_sheet.dart state & flows.
 *
 * Responsibilities:
 *   - load root comments (paginated)
 *   - expand / collapse reply threads per parent
 *   - add comment / reply (optimistic + transactional write, notification fan-out)
 *   - like / unlike comment (owner-guarded, optimistic)
 *   - deep-link highlight via RoadmapCommentInfoEntity
 *   - expose a live numeric comment count + emit ROADMAP_ITEM_COMMENTS_UPDATED
 *
 * Structural sibling of useContentComments. Divergences (both verified against
 * Flutter): the optimistic entity is a RoadmapCommentEntity keyed by
 * roadmapItemId/roadmapUserId (no mediaInfo); the notification carries
 * roadmapItemInfo + roadmapCommentInfo (NOT mediaInfo/commentInfo) and a minimal
 * usersInfo (only userId/name/peerUserId — roadmap_comments_bottom_sheet.dart
 * :524-528 sets no username/peerName); notification types are roadmapComment /
 * roadmapCommentReply / roadmapCommentLike; reply-to-reply popularity is seeded
 * as commentToReply.popularity + DEFAULT_REPLY_POPULARITY_FOR_COMMENT - 0.001.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import { fetchRoadmapRootCommentsUseCase } from '@/domain/usecases/roadmapComment/fetchRoadmapRootCommentsUseCase';
import { fetchRoadmapChildCommentsUseCase } from '@/domain/usecases/roadmapComment/fetchRoadmapChildCommentsUseCase';
import { fetchRoadmapNotificationCommentsUseCase } from '@/domain/usecases/roadmapComment/fetchRoadmapNotificationCommentsUseCase';
import { addRoadmapCommentUseCase } from '@/domain/usecases/roadmapComment/addRoadmapCommentUseCase';
import { likeRoadmapCommentUseCase } from '@/domain/usecases/roadmapComment/likeRoadmapCommentUseCase';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { COMMENTS_CONSTANTS } from '@/domain/constants/commentsConstants';
import { MathUtils } from '@/domain/utils/mathUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';
import type { RoadmapCommentInfoEntity } from '@/domain/entities/info/roadmapCommentInfoEntity';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';
import type { RoadmapCommentLikeDto } from '@/data/dtos/roadmapCommentLike/roadmapCommentLikeDto';
import type { RootState } from '@/presentation/store';

export interface UseRoadmapCommentsParams {
  item: RoadmapItemEntity;
  commentInfo?: RoadmapCommentInfoEntity;
}

export interface UseRoadmapCommentsResult {
  comments: RoadmapCommentEntity[];
  isLoading: boolean;
  hasMore: boolean;
  commentCount: number;
  commentToReply: RoadmapCommentEntity | null;
  highlightedCommentId: string | null;
  loadMore: () => Promise<void>;
  expandReplies: (parent: RoadmapCommentEntity) => Promise<void>;
  collapseReplies: (parent: RoadmapCommentEntity) => void;
  addComment: (text: string) => Promise<boolean>;
  likeComment: (comment: RoadmapCommentEntity, liked: boolean) => Promise<void>;
  setCommentToReply: (comment: RoadmapCommentEntity | null) => void;
  cancelReply: () => void;
}

export function useRoadmapComments(
  params: UseRoadmapCommentsParams,
): UseRoadmapCommentsResult {
  const { item, commentInfo } = params;
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [comments, setComments] = useState<RoadmapCommentEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [commentToReply, setCommentToReply] = useState<RoadmapCommentEntity | null>(null);
  const [commentCount, setCommentCount] = useState<number>(item.comments ?? 0);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);

  // Mirror the live count in a ref so the fan-out emits the same value the title
  // lands on, even across multiple adds within one sheet session. Reading the
  // commentCount state captured in addComment's closure would be stale; the ref
  // is the single source of truth shared with the title.
  const commentCountRef = useRef(commentCount);
  useEffect(() => { commentCountRef.current = commentCount; }, [commentCount]);

  const lastFetchedRef = useRef<PageCursor | undefined>(undefined);
  const mountedRef = useRef(true);
  const loadMoreInFlightRef = useRef(false);
  const expandInFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const roadmapItemId = item.id;

  const loadInitial = useCallback(async () => {
    setIsLoading(true);

    try {
      if (commentInfo?.id) {
        const notifComments = await fetchRoadmapNotificationCommentsUseCase.execute(
          roadmapItemId,
          commentInfo,
        );
        if (!mountedRef.current) return;
        const parent = notifComments.find(c => c.id === commentInfo.parentId);
        const target = notifComments.find(c => c.id === commentInfo.id);
        if (parent) {
          parent.children = notifComments.filter(c => c.parentId === parent.id);
          parent.hasMoreChildren = true;

          setComments([parent]);
          setHighlightedCommentId(
            commentInfo.highlightParentComment ? parent.id : (target?.id ?? parent.id),
          );
        } else if (target) {
          setComments([target]);
          setHighlightedCommentId(target.id);
        }
      }

      const result = await fetchRoadmapRootCommentsUseCase.execute(roadmapItemId);
      if (!mountedRef.current) return;

      setComments(prev => {
        // Merge: keep any deep-linked comment already in state first, then append paged results
        const byId = new Map<string, RoadmapCommentEntity>();
        for (const c of prev) byId.set(c.id, c);
        for (const c of result.comments) if (!byId.has(c.id)) byId.set(c.id, c);
        return Array.from(byId.values());
      });

      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading roadmap comments', 'useRoadmapComments.loadInitial', { error: error as Error });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadmapItemId]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;

    setIsLoading(true);

    try {
      const result = await fetchRoadmapRootCommentsUseCase.execute(roadmapItemId, lastFetchedRef.current);

      if (!mountedRef.current) return;

      setComments(prev => {
        const byId = new Map<string, RoadmapCommentEntity>();
        for (const c of prev) byId.set(c.id, c);
        for (const c of result.comments) if (!byId.has(c.id)) byId.set(c.id, c);
        return Array.from(byId.values());
      });

      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading more roadmap comments', 'useRoadmapComments.loadMore', { error: error as Error });
    } finally {
      loadMoreInFlightRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, [hasMore, roadmapItemId]);

  const expandReplies = useCallback(async (parent: RoadmapCommentEntity) => {
    if (expandInFlightRef.current.has(parent.id)) return;
    expandInFlightRef.current.add(parent.id);

    setComments(prev => prev.map(c =>
      c.id === parent.id ? { ...c, isFetchingChildren: true } : c,
    ));

    try {
      const result = await fetchRoadmapChildCommentsUseCase.execute(
        roadmapItemId,
        parent.id,
        parent.lastFetchedChild ?? undefined,
      );

      if (!mountedRef.current) return;

      setComments(prev => prev.map(c => {
        if (c.id !== parent.id) return c;
        const existing = c.children ?? [];
        const byId = new Map<string, RoadmapCommentEntity>();
        for (const child of existing) byId.set(child.id, child);
        for (const child of result.comments) if (!byId.has(child.id)) byId.set(child.id, child);
        return {
          ...c,
          children: Array.from(byId.values()),
          hasMoreChildren: result.hasMore,
          lastFetchedChild: result.cursor ?? null,
          isFetchingChildren: false,
        };
      }));
    } catch (error) {
      appLogger.error('Error expanding roadmap replies', 'useRoadmapComments.expandReplies', { error: error as Error });
      if (mountedRef.current) {
        setComments(prev => prev.map(c =>
          c.id === parent.id ? { ...c, isFetchingChildren: false } : c,
        ));
      }
    } finally {
      expandInFlightRef.current.delete(parent.id);
    }
  }, [roadmapItemId]);

  // Roadmap notification info differs from the content sibling: it carries
  // roadmapItemInfo + roadmapCommentInfo (NOT mediaInfo + commentInfo) and a
  // minimal usersInfo (only userId/name/peerUserId — roadmap_comments_bottom
  // _sheet.dart:524-528 sets no username/peerName).
  const buildNotificationInfo = useCallback(
    (
      commentId: string,
      parentId: string | null,
      replyId: string | null,
      peerUserId: string,
      notificationType: number,
      isUndo: boolean,
    ): NotificationInfoDto | null => {
      if (!currentUser) return null;

      return {
        usersInfo: {
          userId: currentUser.firebaseId,
          name: currentUser.name,
          peerUserId,
        },
        roadmapItemInfo: { roadmapItemId },
        roadmapCommentInfo: {
          id: commentId,
          roadmapItemId,
          parentId: parentId ?? undefined,
          replyId: replyId ?? undefined,
        },
        notificationType,
        read: false,
        isUndo,
        timestamp: Date.now(),
      };
    },
    [currentUser, roadmapItemId],
  );

  const addComment = useCallback(async (text: string): Promise<boolean> => {
    const trimmed = text.trim();
    if (!trimmed || !currentUser) return false;

    try {
      const commentId = generateUuidV1();
      // parentId is the root comment's id whether replying to a root or a child:
      // commentToReply.parentId (replying to a child) ?? commentToReply.id
      // (replying to a root) ?? null (new root). Matches roadmap_comments_bottom
      // _sheet.dart:446-473 (rootCommentToReply.id).
      const parentId = commentToReply?.parentId ?? commentToReply?.id ?? null;
      const isReplyToReply = !!commentToReply?.parentId;
      const replyToReply = isReplyToReply && commentToReply
        ? { replyId: commentToReply.id, userName: commentToReply.userName, userId: commentToReply.userId }
        : null;

      // Reply-to-reply popularity seeds the optimistic entity so it sorts next to
      // the comment it answers; a fresh root uses the user's popularity. Matches
      // roadmap_comments_bottom_sheet.dart:462-467.
      const popularity = replyToReply && commentToReply
        ? commentToReply.popularity + COMMENTS_CONSTANTS.DEFAULT_REPLY_POPULARITY_FOR_COMMENT - 0.001
        : MathUtils.getUserPopularityForComment(currentUser.popularity);

      const comment: RoadmapCommentEntity = {
        id: commentId,
        parentId,
        text: trimmed,
        commentToReply: replyToReply,
        replies: 0,
        likes: 0,
        popularity,
        createdTimestamp: Date.now(),
        roadmapItemId,
        roadmapUserId: item.userId,
        userId: currentUser.firebaseId,
        userName: currentUser.name,
        username: currentUser.username,
        children: [],
        hasMoreChildren: false,
        childrenFetched: 0,
        isFetchingChildren: false,
      };

      // peerUserId = commentToReply?.userId ?? roadmapUserId; the notification is
      // suppressed when the author is the recipient (roadmap_comments_bottom
      // _sheet.dart:517-519).
      const peerUserId = commentToReply?.userId ?? item.userId;
      const sendCommentNotification = currentUser.firebaseId !== peerUserId;
      const notificationType = parentId
        ? AppNotificationType.roadmapCommentReply
        : AppNotificationType.roadmapComment;

      const notification = buildNotificationInfo(
        commentId,
        parentId,
        replyToReply?.replyId ?? null,
        peerUserId,
        notificationType,
        false,
      );

      // Optimistic insert: prepend a root; push a reply into its parent's children
      // and bump the parent's reply count.
      setComments(prev => {
        if (!parentId) return [comment, ...prev];
        return prev.map(c => {
          if (c.id !== parentId) return c;
          return {
            ...c,
            replies: c.replies + 1,
            children: [...(c.children ?? []), comment],
          };
        });
      });

      const success = await addRoadmapCommentUseCase.execute({
        comment,
        sendCommentNotification,
        notification,
      });

      if (!success) {
        // Roll back optimistic insert
        setComments(prev => {
          if (!parentId) return prev.filter(c => c.id !== commentId);
          return prev.map(c => {
            if (c.id !== parentId) return c;
            return {
              ...c,
              replies: Math.max(0, c.replies - 1),
              children: (c.children ?? []).filter(ch => ch.id !== commentId),
            };
          });
        });
        return false;
      }

      // Every comment add (root OR reply) bumps the roadmap item's comment count,
      // matching Flutter (roadmap_comments_bottom_sheet.dart:498-501 increments
      // roadmapItemEntity.comments with no parentId gate). Compute the new total
      // from the live counter ref (not the frozen item.comments prop) so
      // consecutive adds in one session emit the value the title lands on.
      const newCount = commentCountRef.current + 1;
      setCommentCount(newCount);
      eventBus.emit(APP_EVENT_KEYS.ROADMAP_ITEM_COMMENTS_UPDATED, {
        roadmapItemId,
        comments: newCount,
      });

      setCommentToReply(null);
      return true;
    } catch (error) {
      appLogger.error('Error adding roadmap comment', 'useRoadmapComments.addComment', { error: error as Error });
      return false;
    }
  }, [buildNotificationInfo, commentToReply, currentUser, item.userId, roadmapItemId]);

  const likeComment = useCallback(async (comment: RoadmapCommentEntity, liked: boolean) => {
    if (!currentUser) return;
    // Owner-guard: self-likes are blocked (roadmap_comment_widget.dart:125-127).
    if (currentUser.firebaseId === comment.userId) return;

    // Optimistic toggle on the matching root or child.
    const delta = liked ? 1 : -1;
    setComments(prev => prev.map(c => {
      if (c.id === comment.id) return { ...c, likes: Math.max(0, c.likes + delta) };
      if (c.children?.some(ch => ch.id === comment.id)) {
        return {
          ...c,
          children: c.children.map(ch =>
            ch.id === comment.id ? { ...ch, likes: Math.max(0, ch.likes + delta) } : ch,
          ),
        };
      }
      return c;
    }));

    const like: RoadmapCommentLikeDto = {
      userId: currentUser.firebaseId,
      commentId: comment.id,
      parentCommentId: comment.parentId ?? undefined,
      liked,
      createdDate: Date.now(),
    };

    const notification = buildNotificationInfo(
      comment.id,
      comment.parentId ?? null,
      // replyId is the original reply target only when the like target is a child
      // whose commentToReply points at a different reply (roadmap_comment_widget
      // .dart:172-178). Otherwise no replyId.
      comment.parentId && comment.commentToReply?.replyId !== comment.parentId
        ? comment.commentToReply?.replyId ?? null
        : null,
      comment.userId,
      AppNotificationType.roadmapCommentLike,
      !liked,
    );

    if (!notification) return;

    try {
      const success = await likeRoadmapCommentUseCase.execute({
        like,
        roadmapItemId,
        notification,
      });

      if (!success) {
        // Rollback
        setComments(prev => prev.map(c => {
          if (c.id === comment.id) return { ...c, likes: Math.max(0, c.likes - delta) };
          if (c.children?.some(ch => ch.id === comment.id)) {
            return {
              ...c,
              children: c.children.map(ch =>
                ch.id === comment.id ? { ...ch, likes: Math.max(0, ch.likes - delta) } : ch,
              ),
            };
          }
          return c;
        }));
      }
    } catch (error) {
      appLogger.error('Error liking roadmap comment', 'useRoadmapComments.likeComment', { error: error as Error });
    }
  }, [buildNotificationInfo, currentUser, roadmapItemId]);

  const collapseReplies = useCallback((parent: RoadmapCommentEntity) => {
    setComments(prev => prev.map(c =>
      c.id === parent.id
        ? { ...c, children: [], hasMoreChildren: true, lastFetchedChild: null, isFetchingChildren: false }
        : c,
    ));
  }, []);

  const cancelReply = useCallback(() => setCommentToReply(null), []);

  return {
    comments,
    isLoading,
    hasMore,
    commentCount,
    commentToReply,
    highlightedCommentId,
    loadMore,
    expandReplies,
    collapseReplies,
    addComment,
    likeComment,
    setCommentToReply,
    cancelReply,
  };
}
